import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import {
  api,
  type DateScoringMode,
  type OptionResultInput,
  type PenaltyWinner,
  type QuestionDisplayFormat,
  type QuestionResultsFormat,
  type ResultStatus,
} from '../api.js';
import AnswerAppearanceEditor from '../components/AnswerAppearanceEditor.js';
import DateField from '../components/DateField.js';
import OptionGroup from '../components/OptionGroup.js';
import RadioWithHelp, {
  DATE_SCORING_MODES,
  useDateScoringText,
} from '../components/RadioWithHelp.js';
import type { AnswerAppearance } from '../lib/customQuestionDraft.js';
import { splitDateTime } from '../lib/intl.js';
import styles from './EditQuestionResultsScreen.module.css';

type ResultStatusValue = 'correct' | 'partial' | 'wrong' | '';

/**
 * Organizer-only: edit a question's rules, or — once the real-world outcome
 * is known — enter results (PLAN.md Phase 10.4, split into two entry points
 * in 10.20). Reached from two different buttons on the question card
 * (`OrganizerActions`, `CustomQuestionsSection.tsx`) and two different
 * routes, each rendering only its own section here via `mode` — never both
 * at once, so "I just want to type in the score" and "I want to change the
 * deadline or the answer colours" can never be confused for each other. The
 * two sections' state and mutations stay in one file rather than two
 * because they load the same question and always did; only which section
 * *renders* depends on `mode`.
 *
 * The rules section is shared by both question kinds (deadline,
 * allowLockForever — a `football` question has no scoring mode or point
 * range to edit here). The results section branches by kind: a
 * `multiple_choice` question gets the per-option correct/partial/wrong grid
 * it always had; a `football` question gets a real-score entry instead.
 */
export default function EditQuestionResultsScreen({ mode }: { mode: 'rules' | 'results' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dateScoringText = useDateScoringText();
  const { id = '', poolId = '', questionId = '' } = useParams();

  const { data, isPending } = useQuery({
    queryKey: ['questions', id, poolId],
    queryFn: () => api.getQuestions(id, poolId),
  });
  const question = data?.questions.find((q) => q.id === questionId);

  const [scoringMode, setScoringMode] = useState<'per_answer' | 'winner_loser'>('per_answer');
  const [minPoints, setMinPoints] = useState('');
  const [maxPoints, setMaxPoints] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  const [allowLockForever, setAllowLockForever] = useState(false);
  const [displayFormat, setDisplayFormat] = useState<QuestionDisplayFormat>('list');
  const [resultsFormat, setResultsFormat] = useState<QuestionResultsFormat>('list');
  const [appearanceByOption, setAppearanceByOption] = useState<Record<string, AnswerAppearance>>(
    {},
  );
  const [statusByOption, setStatusByOption] = useState<Record<string, ResultStatusValue>>({});
  const [pointsByOption, setPointsByOption] = useState<Record<string, string>>({});
  const [winnerByOption, setWinnerByOption] = useState<Record<string, boolean>>({});
  const [realHome, setRealHome] = useState('');
  const [realAway, setRealAway] = useState('');
  const [realPenaltyWinner, setRealPenaltyWinner] = useState<PenaltyWinner | null>(null);
  const [dateScoringMode, setDateScoringMode] = useState<DateScoringMode>('placement');
  /** Which mode's "?" explanation is expanded — one at a time, as in the creation editor. */
  const [openScoringHelp, setOpenScoringHelp] = useState<DateScoringMode | null>(null);
  const [dateTargetMonth, setDateTargetMonth] = useState('');
  /** Raw `<input type="datetime-local">` value — kept as text, never turned into a `Date`. */
  const [realDateAt, setRealDateAt] = useState('');
  const [matchOutcome, setMatchOutcome] = useState('');
  const [matchExact, setMatchExact] = useState('');
  const [matchPotMultiplier, setMatchPotMultiplier] = useState('');
  const [matchDecay, setMatchDecay] = useState('');
  const [matchThreshold, setMatchThreshold] = useState('');
  const [matchPenaltyExact, setMatchPenaltyExact] = useState('');
  const [matchPenaltyWrong, setMatchPenaltyWrong] = useState('');

  // Populate the form once the question loads — never again after, so the
  // organizer's in-progress edits aren't clobbered by a background refetch.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (loaded || !question) return;
    if (question.scoringMode) setScoringMode(question.scoringMode);
    setMinPoints(question.minPoints !== null ? String(question.minPoints) : '');
    setMaxPoints(question.maxPoints !== null ? String(question.maxPoints) : '');
    const split = splitDateTime(question.deadlineAt);
    setDeadlineDate(split.date);
    setDeadlineTime(split.time);
    setAllowLockForever(question.allowLockForever);
    setDisplayFormat(question.displayFormat);
    setResultsFormat(question.resultsFormat);
    setAppearanceByOption(
      Object.fromEntries(
        question.options.map((o) => [
          o.id,
          { color: o.color ?? '', emoji: o.emoji ?? '', imageUrl: o.imageUrl ?? '' },
        ]),
      ),
    );
    setStatusByOption(
      Object.fromEntries(question.options.map((o) => [o.id, o.resultStatus ?? ''])),
    );
    setPointsByOption(
      Object.fromEntries(
        question.options.map((o) => [
          o.id,
          o.pointsAwarded !== null ? String(o.pointsAwarded) : '',
        ]),
      ),
    );
    setWinnerByOption(Object.fromEntries(question.options.map((o) => [o.id, o.isWinner])));
    setRealHome(question.realHome !== null ? String(question.realHome) : '');
    setRealAway(question.realAway !== null ? String(question.realAway) : '');
    setRealPenaltyWinner(question.realPenaltyWinner);
    if (question.dateScoringMode) setDateScoringMode(question.dateScoringMode);
    // `<input type="month">` wants `YYYY-MM`; the API stores the month's 1st.
    setDateTargetMonth(question.dateTargetMonth ? question.dateTargetMonth.slice(0, 7) : '');
    // Stored as `YYYY-MM-DD HH:MM:SS`; the input wants `YYYY-MM-DDTHH:MM`.
    // Sliced rather than parsed, so no time zone is ever applied to it.
    setRealDateAt(
      question.realDateAt
        ? `${question.realDateAt.slice(0, 10)}T${question.realDateAt.slice(11, 16)}`
        : '',
    );
    setMatchOutcome(question.matchOutcome !== null ? String(question.matchOutcome) : '');
    setMatchExact(question.matchExact !== null ? String(question.matchExact) : '');
    setMatchPotMultiplier(
      question.matchPotMultiplier !== null ? String(question.matchPotMultiplier) : '',
    );
    setMatchDecay(question.matchDecay !== null ? String(question.matchDecay) : '');
    setMatchThreshold(question.matchThreshold !== null ? String(question.matchThreshold) : '');
    setMatchPenaltyExact(
      question.matchPenaltyExact !== null ? String(question.matchPenaltyExact) : '',
    );
    setMatchPenaltyWrong(
      question.matchPenaltyWrong !== null ? String(question.matchPenaltyWrong) : '',
    );
    setLoaded(true);
  }, [question, loaded]);

  const saveRules = useMutation({
    mutationFn: () =>
      api.updateQuestionRules(id, poolId, questionId, {
        // Layout and per-answer cosmetics only mean anything for
        // multiple_choice — a football question renders a scoreline, which
        // has no answers to arrange or colour. An empty string is the
        // editor's "unset", which the API expects as an explicit null.
        ...(question?.kind === 'multiple_choice'
          ? {
              scoringMode,
              displayFormat,
              resultsFormat,
              optionAppearance: question.options.map((option) => {
                const appearance = appearanceByOption[option.id];
                return {
                  optionId: option.id,
                  color: appearance?.color || null,
                  emoji: appearance?.emoji.trim() || null,
                  imageUrl: appearance?.imageUrl || null,
                };
              }),
            }
          : {}),
        minPoints: minPoints.trim() === '' ? null : Number(minPoints),
        maxPoints: maxPoints.trim() === '' ? null : Number(maxPoints),
        deadlineAt:
          deadlineDate && deadlineTime
            ? new Date(`${deadlineDate}T${deadlineTime}`).toISOString()
            : null,
        allowLockForever,
        ...(question?.kind === 'date'
          ? {
              dateScoringMode,
              dateTargetMonth: dateTargetMonth ? `${dateTargetMonth}-01` : null,
              resultsFormat,
            }
          : {}),
        ...(question?.kind === 'football'
          ? {
              ...(matchOutcome.trim() !== '' ? { matchOutcome: Number(matchOutcome) } : {}),
              ...(matchExact.trim() !== '' ? { matchExact: Number(matchExact) } : {}),
              ...(matchPotMultiplier.trim() !== ''
                ? { matchPotMultiplier: Number(matchPotMultiplier) }
                : {}),
              ...(matchDecay.trim() !== '' ? { matchDecay: Number(matchDecay) } : {}),
              ...(matchThreshold.trim() !== '' ? { matchThreshold: Number(matchThreshold) } : {}),
              ...(matchPenaltyExact.trim() !== ''
                ? { matchPenaltyExact: Number(matchPenaltyExact) }
                : {}),
              ...(matchPenaltyWrong.trim() !== ''
                ? { matchPenaltyWrong: Number(matchPenaltyWrong) }
                : {}),
            }
          : {}),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', id, poolId] }),
  });

  const realHomeNum = Number.parseInt(realHome, 10);
  const realAwayNum = Number.parseInt(realAway, 10);
  const realBothEntered =
    realHome.trim() !== '' &&
    realAway.trim() !== '' &&
    !Number.isNaN(realHomeNum) &&
    !Number.isNaN(realAwayNum);
  const realIsLevel = realBothEntered && realHomeNum === realAwayNum;
  const needsRealPenaltyPick = realIsLevel && (question?.canGoToPenalties ?? false);

  const saveResults = useMutation({
    mutationFn: () => {
      if (!question) throw new Error('Question not loaded');
      if (question.kind === 'football') {
        if (!realBothEntered) throw new Error('Enter both scores');
        return api.revealFootballQuestionResult(id, poolId, questionId, {
          realHome: realHomeNum,
          realAway: realAwayNum,
          ...(needsRealPenaltyPick && realPenaltyWinner ? { realPenaltyWinner } : {}),
        });
      }
      if (question.kind === 'date') {
        if (realDateAt.trim() === '') throw new Error('Enter the real date');
        // Sent exactly as typed — see `DateResultInput`'s doc comment for why
        // this must never go through `toISOString()`.
        return api.revealDateQuestionResult(id, poolId, questionId, { realDateAt });
      }
      const results: OptionResultInput[] = question.options.map((option) => {
        if (scoringMode === 'per_answer') {
          const status = statusByOption[option.id];
          const points = pointsByOption[option.id];
          return {
            optionId: option.id,
            ...(status ? { resultStatus: status as ResultStatus } : {}),
            ...(points && points.trim() !== '' ? { pointsAwarded: Number(points) } : {}),
          };
        }
        return { optionId: option.id, isWinner: winnerByOption[option.id] ?? false };
      });
      return api.revealQuestionResults(id, poolId, questionId, results);
    },
    onSuccess: async () => {
      // Revealing results changes both what the question card shows and
      // what the leaderboard totals — a stale leaderboard fetched before
      // this reveal would otherwise keep showing pre-reveal numbers.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['questions', id, poolId] }),
        queryClient.invalidateQueries({ queryKey: ['custom-leaderboard', id, poolId] }),
      ]);
      void navigate(`/groups/${id}/bolaos/${poolId}/predictions`);
    },
  });

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (!question) {
    return (
      <main className="screen">
        <Link to={`/groups/${id}/bolaos/${poolId}/predictions`} className="muted">
          {t('common.back')}
        </Link>
        <p className="error">{t('common.somethingWentWrong')}</p>
      </main>
    );
  }

  const isFootball = question.kind === 'football';
  const isDate = question.kind === 'date';
  const isSourceLinked = isFootball && question.isSourceLinked;
  const canSaveResults = isFootball
    ? realBothEntered &&
      (!needsRealPenaltyPick || realPenaltyWinner !== null) &&
      !saveResults.isPending
    : isDate
      ? realDateAt.trim() !== '' && !saveResults.isPending
      : !saveResults.isPending;

  return (
    <main className="screen">
      <Link to={`/groups/${id}/bolaos/${poolId}/predictions`} className="muted">
        {t('common.back')}
      </Link>
      <h1>
        {mode === 'rules'
          ? t('customCompetition.editQuestionTitle')
          : t('customCompetition.enterResultsTitle')}
      </h1>
      <p className="muted">{question.prompt}</p>

      {mode === 'rules' ? (
        <div className={`panel ${styles.section}`}>
          <h2 className={styles.sectionTitle}>{t('customCompetition.rulesSectionTitle')}</h2>

          {isDate ? (
            <>
              <OptionGroup title={t('customCompetition.groupScoring')} defaultOpen>
                <div>
                  <p className="label">{t('customCompetition.dateScoringLabel')}</p>
                  <div className={styles.radioGroup}>
                    {/* Same control the creation editor uses, "?" explanations
                      included — an organizer changing the mode afterwards needs
                      the same help they had when first choosing it. */}
                    {DATE_SCORING_MODES.map((mode) => (
                      <RadioWithHelp
                        key={mode}
                        name="date-scoring"
                        checked={dateScoringMode === mode}
                        label={dateScoringText[mode].label}
                        help={dateScoringText[mode].help}
                        helpOpen={openScoringHelp === mode}
                        onToggleHelp={() =>
                          setOpenScoringHelp((current) => (current === mode ? null : mode))
                        }
                        onSelect={() => setDateScoringMode(mode)}
                      />
                    ))}
                  </div>
                </div>

                {/* `placement` pays nothing, so a range would be a rule nobody
                  applies — same reasoning as the creation editor. */}
                {dateScoringMode !== 'placement' ? (
                  <div>
                    <p className="label">{t('customCompetition.pointsRangeLabel')}</p>
                    <div className={styles.rangeRow}>
                      <input
                        className="input"
                        type="number"
                        step={1}
                        value={minPoints}
                        aria-label={t('customCompetition.minPoints')}
                        placeholder={t('customCompetition.minPointsPlaceholder')}
                        onChange={(e) => setMinPoints(e.target.value)}
                      />
                      <span className={styles.rangeSeparator}>–</span>
                      <input
                        className="input"
                        type="number"
                        step={1}
                        value={maxPoints}
                        aria-label={t('customCompetition.maxPoints')}
                        placeholder={t('customCompetition.maxPointsPlaceholder')}
                        onChange={(e) => setMaxPoints(e.target.value)}
                      />
                    </div>
                    <p className="muted" style={{ fontSize: '0.75rem' }}>
                      {t('customCompetition.dateRangeHint')}
                    </p>
                  </div>
                ) : null}
              </OptionGroup>

              <OptionGroup title={t('customCompetition.groupAppearance')}>
                <div>
                  <label className="label" htmlFor="dateTargetMonth">
                    {t('customCompetition.dateTargetMonthLabel')}
                  </label>
                  <DateField
                    id="dateTargetMonth"
                    type="month"
                    value={dateTargetMonth}
                    onChange={setDateTargetMonth}
                  />
                </div>

                <div>
                  <p className="label">{t('customCompetition.resultsFormatLabel')}</p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={resultsFormat === 'leaderboard'}
                        onChange={() => setResultsFormat('leaderboard')}
                      />
                      {t('customCompetition.resultsFormatLeaderboard')}
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={resultsFormat !== 'leaderboard'}
                        onChange={() => setResultsFormat('list')}
                      />
                      {t('customCompetition.resultsFormatList')}
                    </label>
                  </div>
                </div>
              </OptionGroup>
            </>
          ) : null}

          {question.kind === 'multiple_choice' ? (
            <>
              <OptionGroup title={t('customCompetition.groupScoring')} defaultOpen>
                <div>
                  <p className="label">{t('customCompetition.pointsSystemLabel')}</p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={scoringMode === 'per_answer'}
                        onChange={() => setScoringMode('per_answer')}
                      />
                      {t('customCompetition.pointsSystemPerAnswer')}
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={scoringMode === 'winner_loser'}
                        onChange={() => setScoringMode('winner_loser')}
                      />
                      {t('customCompetition.pointsSystemWinnerLoser')}
                    </label>
                  </div>
                </div>

                {scoringMode === 'per_answer' ? (
                  <div>
                    <p className="label">{t('customCompetition.pointsRangeLabel')}</p>
                    <div className={styles.rangeRow}>
                      <input
                        className="input"
                        type="number"
                        step={1}
                        value={minPoints}
                        aria-label={t('customCompetition.minPoints')}
                        placeholder={t('customCompetition.minPointsPlaceholder')}
                        onChange={(e) => setMinPoints(e.target.value)}
                      />
                      <span className={styles.rangeSeparator}>–</span>
                      <input
                        className="input"
                        type="number"
                        step={1}
                        value={maxPoints}
                        aria-label={t('customCompetition.maxPoints')}
                        placeholder={t('customCompetition.maxPointsPlaceholder')}
                        onChange={(e) => setMaxPoints(e.target.value)}
                      />
                    </div>
                  </div>
                ) : null}
              </OptionGroup>

              {/* Layout and per-answer cosmetics — the same controls the
                creation screen offers, so a colour or emoji chosen wrong is
                fixable rather than permanent. Answer LABELS are deliberately
                not editable here: people have already answered by the time
                this screen matters, and rewording an answer under them would
                change what they picked. */}
              <OptionGroup title={t('customCompetition.groupAppearance')}>
                <div>
                  <p className="label">{t('customCompetition.displayFormatLabel')}</p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={displayFormat === 'list'}
                        onChange={() => setDisplayFormat('list')}
                      />
                      {t('customCompetition.displayFormatList')}
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={displayFormat === 'card'}
                        onChange={() => setDisplayFormat('card')}
                      />
                      {t('customCompetition.displayFormatCard')}
                    </label>
                  </div>
                </div>

                <div>
                  <p className="label">{t('customCompetition.resultsFormatLabel')}</p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={resultsFormat === 'list'}
                        onChange={() => setResultsFormat('list')}
                      />
                      {t('customCompetition.resultsFormatList')}
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={resultsFormat === 'panels'}
                        onChange={() => setResultsFormat('panels')}
                      />
                      {t('customCompetition.resultsFormatPanels')}
                    </label>
                  </div>
                </div>

                {displayFormat === 'card' ? (
                  <div className={styles.appearanceList}>
                    {question.options.map((option) => (
                      <div key={option.id}>
                        <p className={styles.appearanceLabel}>{option.label}</p>
                        <AnswerAppearanceEditor
                          value={
                            appearanceByOption[option.id] ?? { color: '', emoji: '', imageUrl: '' }
                          }
                          onChange={(patch) =>
                            setAppearanceByOption((current) => ({
                              ...current,
                              [option.id]: {
                                ...(current[option.id] ?? { color: '', emoji: '', imageUrl: '' }),
                                ...patch,
                              },
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </OptionGroup>
            </>
          ) : isFootball ? (
            // The seven `MatchParams` a real match's ruleset stage would assign
            // it — football's alone, and meaningless for any other kind. Same
            // category label as the other two kinds' point-related settings,
            // since that's what these are: how this question scores.
            <OptionGroup title={t('customCompetition.groupScoring')}>
              <div className={styles.advancedGrid}>
                <p className="muted" style={{ fontSize: '0.75rem', gridColumn: '1 / -1' }}>
                  {t('customCompetition.advancedSettingsHint')}
                </p>

                <label className={styles.advancedField}>
                  {t('customCompetition.matchOutcomeLabel')}
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={1}
                    value={matchOutcome}
                    onChange={(e) => setMatchOutcome(e.target.value)}
                  />
                </label>

                <label className={styles.advancedField}>
                  {t('customCompetition.matchExactLabel')}
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={1}
                    value={matchExact}
                    onChange={(e) => setMatchExact(e.target.value)}
                  />
                </label>

                <label className={styles.advancedField}>
                  {t('customCompetition.matchPotMultiplierLabel')}
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={0.1}
                    value={matchPotMultiplier}
                    onChange={(e) => setMatchPotMultiplier(e.target.value)}
                  />
                </label>

                <label className={styles.advancedField}>
                  {t('customCompetition.matchDecayLabel')}
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={0.1}
                    value={matchDecay}
                    onChange={(e) => setMatchDecay(e.target.value)}
                  />
                </label>

                <label className={styles.advancedField}>
                  {t('customCompetition.matchThresholdLabel')}
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={1}
                    value={matchThreshold}
                    onChange={(e) => setMatchThreshold(e.target.value)}
                  />
                </label>

                <label className={styles.advancedField}>
                  {t('customCompetition.matchPenaltyExactLabel')}
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={1}
                    value={matchPenaltyExact}
                    onChange={(e) => setMatchPenaltyExact(e.target.value)}
                  />
                </label>

                <label className={styles.advancedField}>
                  {t('customCompetition.matchPenaltyWrongLabel')}
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={1}
                    value={matchPenaltyWrong}
                    onChange={(e) => setMatchPenaltyWrong(e.target.value)}
                  />
                </label>
              </div>
            </OptionGroup>
          ) : null}

          <OptionGroup title={t('customCompetition.groupTiming')}>
            <div>
              <label className="label" htmlFor="deadline-date">
                {t('customCompetition.deadlineLabel')}
              </label>
              <div className={styles.deadlineRow}>
                <DateField
                  id="deadline-date"
                  type="date"
                  value={deadlineDate}
                  onChange={setDeadlineDate}
                />
                <DateField
                  type="time"
                  value={deadlineTime}
                  ariaLabel={t('customCompetition.deadlineTimeLabel')}
                  onChange={setDeadlineTime}
                />
              </div>
            </div>

            <label className={styles.lockToggle}>
              <input
                type="checkbox"
                checked={allowLockForever}
                onChange={(e) => setAllowLockForever(e.target.checked)}
              />
              {t('customCompetition.allowLockForeverLabel')}
            </label>
          </OptionGroup>

          {saveRules.isError ? <p className="error">{t('common.somethingWentWrong')}</p> : null}
          <button
            type="button"
            className="button button-secondary"
            disabled={saveRules.isPending}
            onClick={() => saveRules.mutate()}
          >
            {saveRules.isPending
              ? t('customCompetition.savingRules')
              : t('customCompetition.saveRules')}
          </button>
          {saveRules.isSuccess ? (
            <p className={styles.savedNote}>{t('customCompetition.rulesSaved')}</p>
          ) : null}
        </div>
      ) : null}

      {mode === 'results' ? (
        <div className={`panel ${styles.section}`}>
          <h2 className={styles.sectionTitle}>{t('customCompetition.resultsSectionTitle')}</h2>

          {isSourceLinked ? (
            <>
              <p className="muted" style={{ fontSize: '0.85rem' }}>
                {t('customCompetition.resultsSourceLinkedHint')}
              </p>
              {question.revealedAt && question.realHome !== null && question.realAway !== null ? (
                <p className={styles.savedNote}>
                  {t('customCompetition.resultsSourceLinkedRevealed', {
                    home: question.realHome,
                    away: question.realAway,
                  })}
                </p>
              ) : (
                <p className="muted" style={{ fontSize: '0.85rem' }}>
                  {t('customCompetition.resultsSourceLinkedPending')}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="muted" style={{ fontSize: '0.8rem' }}>
                {t('customCompetition.resultsSectionHint')}
              </p>

              {isFootball ? (
                <div className={styles.rangeRow}>
                  <input
                    className="input"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={20}
                    value={realHome}
                    aria-label={t('predictions.homeScoreLabel', {
                      team: question.homeTeamName ?? '',
                    })}
                    onChange={(e) => setRealHome(e.target.value)}
                  />
                  <span className={styles.rangeSeparator}>×</span>
                  <input
                    className="input"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={20}
                    value={realAway}
                    aria-label={t('predictions.awayScoreLabel', {
                      team: question.awayTeamName ?? '',
                    })}
                    onChange={(e) => setRealAway(e.target.value)}
                  />
                </div>
              ) : isDate ? (
                <div>
                  <label className="label" htmlFor="realDateAt">
                    {t('customCompetition.dateRealAtLabel')}
                  </label>
                  {/* `datetime-local`, and its raw value is what gets sent — the
                organizer types the moment as it happened locally, which is
                exactly how it is stored and scored. */}
                  <DateField
                    id="realDateAt"
                    type="datetime-local"
                    value={realDateAt}
                    onChange={setRealDateAt}
                  />
                  <p className="muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    {t('customCompetition.dateRealAtHint')}
                  </p>
                </div>
              ) : (
                <div className={styles.resultsList}>
                  {question.options.map((option) => (
                    <div key={option.id} className={styles.resultRow}>
                      <span className={styles.resultLabel}>{option.label}</span>
                      {scoringMode === 'per_answer' ? (
                        <>
                          <select
                            className="select"
                            value={statusByOption[option.id] ?? ''}
                            onChange={(e) =>
                              setStatusByOption((s) => ({
                                ...s,
                                [option.id]: e.target.value as ResultStatusValue,
                              }))
                            }
                          >
                            <option value="">
                              {t('customCompetition.resultStatusPlaceholder')}
                            </option>
                            <option value="correct">{t('customCompetition.result.correct')}</option>
                            <option value="partial">{t('customCompetition.result.partial')}</option>
                            <option value="wrong">{t('customCompetition.result.wrong')}</option>
                          </select>
                          <input
                            className={`input ${styles.pointsInput}`}
                            type="number"
                            step={1}
                            value={pointsByOption[option.id] ?? ''}
                            placeholder={t('customCompetition.pointsPlaceholder')}
                            aria-label={t('customCompetition.pointsIfSelected')}
                            onChange={(e) =>
                              setPointsByOption((p) => ({ ...p, [option.id]: e.target.value }))
                            }
                          />
                        </>
                      ) : (
                        <label className={styles.winnerCheckbox}>
                          <input
                            type="checkbox"
                            checked={winnerByOption[option.id] ?? false}
                            onChange={(e) =>
                              setWinnerByOption((w) => ({ ...w, [option.id]: e.target.checked }))
                            }
                          />
                          {t('customCompetition.winnerLabel')}
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {isFootball && needsRealPenaltyPick ? (
                <div>
                  <p className="label">{t('predictions.penaltyPrompt')}</p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={realPenaltyWinner === 'home'}
                        onChange={() => setRealPenaltyWinner('home')}
                      />
                      {t('predictions.penaltyWinnerLabel', { team: question.homeTeamName ?? '' })}
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={realPenaltyWinner === 'away'}
                        onChange={() => setRealPenaltyWinner('away')}
                      />
                      {t('predictions.penaltyWinnerLabel', { team: question.awayTeamName ?? '' })}
                    </label>
                  </div>
                </div>
              ) : null}

              {saveResults.isError ? (
                <p className="error">{t('common.somethingWentWrong')}</p>
              ) : null}
              <button
                type="button"
                className="button button-primary"
                disabled={!canSaveResults}
                onClick={() => saveResults.mutate()}
              >
                {saveResults.isPending
                  ? t('customCompetition.savingResults')
                  : t('customCompetition.saveResultsAndReveal')}
              </button>
            </>
          )}
        </div>
      ) : null}
    </main>
  );
}
